import { Ref } from "@typegoose/typegoose";
import { ObjectId } from "mongoose";
import { SensorConfigurationsNoExplicitKeys } from "./Configurations/SensorConfigurations/SensorConfiguration.js";
import { ExternalLink as ExternalLinkSchema } from "./schemas/commonSubSchemas/externalLink.js";

export enum ONLINE_STATUS {
    Online = "Online",
    Offline = "Offline",
    AwaitingConnection = "Awaiting Connection",
    LiveMonitoringNotSupported = "Live Monitoring not supported",
}

export enum AlertSetting {
    Ignore = 0,
    AddToQueue = 1,
    SMSEveryHour = 10,
    SMSImmediate = 100,
    SMSImmediateEmergency = 200,
}
export type Milliseconds = number;
export type Seconds = number;
export type Minutes = number;
export type BitDepth = number;
export type SproutSensorID = number;

// Automation-related types
export type SensorRawDataFormat = "ADC" | "PWM" | "TEMPHUM" | "ACCEL" | "PULSE_TOTAL";

export type SensorType = keyof typeof SensorConfigurationsNoExplicitKeys;

export type ManufacturerName =
    | "Verdi"
    | "Badger Meter"
    | "Irrometer"
    | "Meter Group"
    | "Dragino"
    | "Sensirion"
    | "McCrometer"
    | "Vegetronix"
    | "Sentek Technologies"
    | "EnviroPro"
    | "HSTI"
    | "AquaCheck USA"
    | "FloraPulse"
    | "GroPoint"
    | "Delta-T"
    | "AquaSpy"
    | "CropX"
    | "Stevens Water Monitoring Systems"
    | "TeeJet Technologies"
    | "Decagon Devices"
    | "Ag Leader Technology"
    | "Soil Scout"
    | "Rivulis"
    | "John Deere"
    | "Keller"
    | "Global Water"
    | "Paremtech"
    | "Flomec"
    | "Honeywell"
    | "Amphenol"
    | "TE Connectivity"
    | "ePlant"
    | "Sensoterra"
    | "RAK Wireless"
    | "Davis Instruments"
    | "Unknown";

export type FirstPartyDeviceType =
    | "sap_1v"
    | "sap_2v"
    | "sap_sense" //sap
    | "spr_block"
    | "spr_1v"
    | "spr_2v"
    | "spr_sense" //sprout
    | "gen4_block"; // gen 4

// eslint-disable-next-line
export type DocumentReference = string | Ref<any> | ObjectId;

// External Link type used by external consumers. The canonical schema now lives in commonSubSchemas/externalLink.ts.
export type ExternalLink = Pick<ExternalLinkSchema, "key" | "name" | "url">;

// Config Info for preset links for each device
export type ExternalLinkConfigInfo = {
    friendlyName: string;
    sampleURL: string;
};

// Look up all external links possible and sample links to include so that we can use them in the UI
const lookupInfoByExternalLinkKey: Record<string, ExternalLinkConfigInfo> = {
    RemoteIT: {
        friendlyName: "Remote IT",
        sampleURL: "https://app.remote.it/#/devices/90:00:00:00:00:01:05:84/details",
    },
    Teal: {
        friendlyName: "Teal",
        sampleURL: "https://customer.teal.global/onechip-cards/89044045817727484800000104108073/info",
    },

    TTIEndDevice: {
        friendlyName: "TTI End Device",
        sampleURL:
            "https://verdi-expeditions.nam1.cloud.thethings.industries/console/applications/sprout-mv-1/devices/eui-0004a30b00ea9583",
    },
    TTIGateway: {
        friendlyName: "TTI Gateway",
        sampleURL: "https://verdi-expeditions.nam1.cloud.thethings.industries/console/gateways/eui-a84041ffff23a7ec",
    },
};

// Predefined External Links for Verdi Gateways (dragino dl0s8n)
export const gatewayExternalLinks: Record<string, ExternalLinkConfigInfo> = {
    TTIGateway: lookupInfoByExternalLinkKey.TTIGateway,
    Teal: lookupInfoByExternalLinkKey.Teal,
    RemoteIT: lookupInfoByExternalLinkKey.RemoteIT,
};

// Predefined External Links for LoRa devices
export const loraDeviceExternalLinks: Record<string, ExternalLinkConfigInfo> = {
    TTIEndDevice: lookupInfoByExternalLinkKey.TTIEndDevice,
};
